import { createContext, useContext, useRef, useState, useCallback } from "react";
import api from "../lib/api";

export const LANGS = [
  { code: "it", label: "IT", flag: "🇮🇹" },
  { code: "en", label: "EN", flag: "🇬🇧" },
  { code: "de", label: "DE", flag: "🇩🇪" },
  { code: "pl", label: "PL", flag: "🇵🇱" },
];

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("lang") || "it");
  const [cache, setCache] = useState({}); // { [lang]: { [itText]: translated } }
  const pending = useRef(new Set());
  const timer = useRef(null);

  const setLang = (l) => { localStorage.setItem("lang", l); setLangState(l); };

  const flush = useCallback(async (targetLang) => {
    timer.current = null;
    const texts = [...pending.current];
    pending.current = new Set();
    if (!texts.length) return;
    try {
      const { data } = await api.post("/translate", { texts, target: targetLang });
      setCache((c) => {
        const lc = { ...(c[targetLang] || {}) };
        texts.forEach((t, i) => { lc[t] = data.translations[i] ?? t; });
        return { ...c, [targetLang]: lc };
      });
    } catch (e) { /* keep Italian fallback */ }
  }, []);

  const t = useCallback((text) => {
    if (!text || typeof text !== "string" || lang === "it") return text;
    const lc = cache[lang] || {};
    if (lc[text] !== undefined) return lc[text];
    if (!pending.current.has(text)) {
      pending.current.add(text);
      if (!timer.current) timer.current = setTimeout(() => flush(lang), 150);
    }
    return text;
  }, [lang, cache, flush]);

  return <I18nCtx.Provider value={{ lang, setLang, t, LANGS }}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx) || { lang: "it", setLang: () => {}, t: (x) => x, LANGS };
